/** Map Mistral markdown bullets into dashboard UI structures; metrics inherit from local derived rows when available. */

export const bulletsToExecutiveCards = (bullets, localCards = []) => {
  if (!bullets?.length) return null;
  return bullets.slice(0, 4).map((text, i) => {
    const local = localCards[i] || {};
    const title = text.split(/[:.]/)[0].slice(0, 55) || local.title || `Insight ${i + 1}`;
    return {
      id: local.id || `ai-${i}`,
      title: title.length > 52 ? `${title.slice(0, 49)}…` : title,
      body: text,
      metric: local.metric || "—",
      metricUnit: local.metricUnit || "",
      badge: local.badge || "AI",
      badgeClass: local.badgeClass || "",
      iconClass: local.iconClass || "",
    };
  });
};

export const bulletsToRootCauseRows = (bullets, localRows = []) => {
  if (!bullets?.length) return null;
  return bullets.slice(0, 5).map((text, i) => {
    const local = localRows[i] || {};
    const label = (text.split(/[:.]/)[0] || local.label || `Signal ${i + 1}`).slice(0, 42);
    return {
      label,
      width: local.width ?? 0,
      value: local.value ?? "—",
      tone: local.tone ?? "",
    };
  });
};

export const bulletsToEnrolmentRecommendations = (bullets, localRecs = []) => {
  if (!bullets?.length) return null;
  return bullets.slice(0, 4).map((body, i) => {
    const local = localRecs[i] || {};
    const title = body.split(/[:.]/)[0].slice(0, 50) || local.title || `Action ${i + 1}`;
    return {
      num: local.num || String(i + 1),
      tone: local.tone ?? "",
      title: title.length > 48 ? `${title.slice(0, 45)}…` : title,
      body,
      badge: local.badge || "—",
      badgeClass: local.badgeClass || "",
    };
  });
};

export const bulletsToPriorityItems = (bullets, localItems = []) => {
  if (!bullets?.length) return null;
  return bullets.slice(0, 4).map((text, i) => {
    const local = localItems[i] || {};
    const title = (text.split(/[:.]/)[0] || local.title || `Action ${i + 1}`).slice(0, 50);
    return {
      rank: local.rank || `P${i + 1}`,
      tone: local.tone ?? "",
      title: title.length > 48 ? `${title.slice(0, 45)}…` : title,
      body: text,
      badge: local.badge || (i === 0 ? "Critical" : i === 1 ? "Watch" : "Routine"),
      badgeClass: local.badgeClass || (i === 0 ? "red" : i === 1 ? "amber" : "green"),
    };
  });
};

export const bulletsToActionRegister = (bullets, localRegister = []) => {
  if (!bullets?.length) return null;
  return bullets.slice(0, 4).map((text, i) => {
    const local = localRegister[i] || {};
    return {
      priority: local.priority || (i < 2 ? "P1" : "P2"),
      theme: local.theme || "General",
      signal: text.split(/[:.]/)[0].slice(0, 60),
      action: text,
      owner: local.owner || "Block Officers",
      status: local.status || (i === 0 ? "Critical" : "Planned"),
      statusClass: local.statusClass || (i === 0 ? "red" : "amber"),
    };
  });
};

export const bulletsToPriorityAlert = (bullets, localAlert = { show: false }) => {
  if (!bullets?.length) return localAlert;
  const first = bullets[0];
  return {
    ...localAlert,
    title: (first.split(/[:.]/)[0] || localAlert.title || "Priority signal").slice(0, 80),
    body: first,
  };
};

export const bulletsToHeroCopy = (bullets, localHero = {}) => {
  if (!bullets?.length) return localHero;
  const primary = bullets[0] || "";
  const secondary = bullets[1] || bullets[0] || "";
  const title = (primary.split(/[:.]/)[0] || localHero.title || "AI insight").slice(0, 72);
  return {
    title,
    body: secondary || primary,
    primaryAction: bullets[2] || localHero.primaryAction || "",
    belowTarget: localHero.belowTarget,
  };
};

export const bulletsToInsightCards = (bullets, localInsights = [], iconKeys = []) => {
  if (!bullets?.length) return null;
  return bullets.slice(0, 4).map((text, i) => {
    const local = localInsights[i] || {};
    const title = text.split(/[:.]/)[0].slice(0, 50) || local.title || `Insight ${i + 1}`;
    return {
      id: local.id || iconKeys[i] || `ai-${i}`,
      title: title.length > 48 ? `${title.slice(0, 45)}…` : title,
      body: text,
      stat: local.stat,
      statSuffix: local.statSuffix,
      iconClass: local.iconClass || "",
    };
  });
};

export const bulletsToActionQueue = (bullets, localActions = []) => {
  if (!bullets?.length) return null;
  const iconClasses = ["orange", "pink", "purple", "green"];
  return bullets.slice(0, 4).map((text, i) => {
    const local = localActions[i] || {};
    const title = text.split(/[:.]/)[0].slice(0, 50) || local.title || `Action ${i + 1}`;
    return {
      iconClass: local.iconClass || iconClasses[i] || "orange",
      title: title.length > 48 ? `${title.slice(0, 45)}…` : title,
      body: text,
    };
  });
};

export const bulletsToHotspots = (bullets, localHotspots = []) => {
  if (!bullets?.length) return null;
  return bullets.slice(0, 5).map((text, i) => {
    const local = localHotspots[i] || {};
    const title = text.split(/[:.]/)[0].slice(0, 55) || local.title || `Hotspot ${i + 1}`;
    return {
      rank: local.rank ?? i + 1,
      rankClass: local.rankClass || "orange",
      title: title.length > 52 ? `${title.slice(0, 49)}…` : title,
      body: text,
      tag: local.tag || "amber",
      tagLabel: local.tagLabel || "Watch",
    };
  });
};

export const bulletsToAlertCopy = (bullets, localAlert = { show: false }) => {
  if (!bullets?.length) return localAlert;
  const first = bullets[0];
  return {
    ...localAlert,
    title: (first.split(/[:.]/)[0] || localAlert.title || "Retention alert").slice(0, 72),
    body: first,
  };
};

export const bulletsToApaarSummaryCards = (bullets, localCards = []) => {
  if (!bullets?.length) return null;
  const severities = ["Healthy", "Backlog", "Consent Gap", "Quality"];
  const severityClasses = ["good", "medium", "medium", "high"];
  const iconClasses = ["green", "orange", "purple", "red"];
  return bullets.slice(0, 4).map((text, i) => {
    const local = localCards[i] || {};
    const title = text.split(/[:.]/)[0].slice(0, 55) || local.title || `Insight ${i + 1}`;
    return {
      id: local.id || `ai-${i}`,
      title: title.length > 52 ? `${title.slice(0, 49)}…` : title,
      body: text,
      severity: local.severity || severities[i] || "Insight",
      severityClass: local.severityClass || severityClasses[i] || "",
      iconClass: local.iconClass || iconClasses[i] || "",
      progress: local.progress ?? 50,
      progressColor: local.progressColor || "var(--apx-accent)",
      metaLabel: local.metaLabel || "—",
      metaValue: local.metaValue || "—",
    };
  });
};

export const bulletsToApaarRootCauseSignals = (bullets, localRows = []) => {
  if (!bullets?.length) return null;
  return bullets.slice(0, 5).map((text, i) => {
    const local = localRows[i] || {};
    const label = (text.split(/[:.]/)[0] || local.label || `Signal ${i + 1}`).slice(0, 42);
    return {
      label: local.label || label,
      widthPct: local.widthPct ?? 0,
      barClass: local.barClass ?? "",
      value: local.value ?? "—",
    };
  });
};

export const bulletsToApaarAiFindings = (bullets, localFindings = []) => {
  if (!bullets?.length) return null;
  const iconClasses = ["orange", "purple", "red"];
  const owners = ["BEO", "HM", "Data Cell"];
  return bullets.slice(0, 3).map((text, i) => {
    const local = localFindings[i] || {};
    const title = text.split(/[:.]/)[0].slice(0, 50) || local.title || `Finding ${i + 1}`;
    return {
      title: title.length > 48 ? `${title.slice(0, 45)}…` : title,
      body: text,
      iconClass: local.iconClass || iconClasses[i] || "orange",
      owner: local.owner || owners[i] || "Officer",
    };
  });
};

export const bulletsToApaarTrackerRows = (bullets, localRows = []) => {
  if (!bullets?.length) return null;
  return bullets.slice(0, 5).map((text, i) => {
    const local = localRows[i] || {};
    return {
      priority: local.priority ?? i + 1,
      insight: text.split(/[:.]/)[0].slice(0, 60) || local.insight || `Insight ${i + 1}`,
      evidence: local.evidence || "—",
      owner: local.owner || "Block Officers",
      action: text,
      status: local.status || (i === 0 ? "Critical" : "Planned"),
      tagClass: local.tagClass || (i === 0 ? "red" : ""),
    };
  });
};

export const bulletsToDropboxSummaryCards = (bullets, localCards = []) => {
  if (!bullets?.length) return null;
  return bullets.slice(0, 4).map((text, i) => {
    const local = localCards[i] || {};
    const title = text.split(/[:.]/)[0].slice(0, 55) || local.title || `Insight ${i + 1}`;
    return {
      id: local.id || `ai-${i}`,
      severity: local.severity || "Insight",
      severityClass: local.severityClass || "",
      iconClass: local.iconClass || "",
      title: title.length > 52 ? `${title.slice(0, 49)}…` : title,
      body: text,
      signals: local.signals || [{ label: text }],
    };
  });
};

export const bulletsToDropboxNarratives = (bullets, localNarratives = []) => {
  if (!bullets?.length) return null;
  const iconClasses = ["green", "red", "orange", "purple"];
  return bullets.slice(0, 4).map((text, i) => {
    const local = localNarratives[i] || {};
    const title = text.split(/[:.]/)[0].slice(0, 55) || local.title || `Signal ${i + 1}`;
    return {
      id: local.id || `ai-${i}`,
      iconClass: local.iconClass || iconClasses[i] || "orange",
      title: title.length > 52 ? `${title.slice(0, 49)}…` : title,
      body: text,
      value: local.value || "—",
      valueLabel: local.valueLabel || "",
    };
  });
};

export const bulletsToTextList = (bullets, localItems = []) => {
  if (!bullets?.length) return null;
  return bullets.slice(0, localItems.length || 4).map((text, i) => {
    const local = localItems[i] || {};
    return {
      ...local,
      text: text || local.text || "",
    };
  });
};

export const bulletsToSignalCards = (bullets, localItems = []) => {
  if (!bullets?.length) return null;
  const badgeClasses = ["high", "low", "medium"];
  const badges = ["High Priority", "Positive Signal", "Watchlist"];
  return bullets.slice(0, 4).map((text, i) => {
    const local = localItems[i] || {};
    const title = text.split(/[:.]/)[0].slice(0, 55) || local.title || `Signal ${i + 1}`;
    return {
      id: local.id || `ai-${i}`,
      title: title.length > 52 ? `${title.slice(0, 49)}…` : title,
      body: text,
      badge: local.badge || badges[i] || "Insight",
      badgeClass: local.badgeClass || badgeClasses[i] || "medium",
      iconClass: local.iconClass || "",
      stats: local.stats || [],
    };
  });
};

export const bulletsToNarrativeItems = (bullets, localItems = []) => {
  if (!bullets?.length) return null;
  return bullets.slice(0, 4).map((text, i) => {
    const local = localItems[i] || {};
    const title = text.split(/[:.]/)[0].slice(0, 55) || local.title || `Insight ${i + 1}`;
    return {
      title: title.length > 52 ? `${title.slice(0, 49)}…` : title,
      body: text,
    };
  });
};

export const bulletsToRootCauseCards = (bullets, localItems = []) => {
  if (!bullets?.length) return null;
  const impacts = ["High", "High", "Medium", "Medium"];
  const iconClasses = ["purple", "orange", "green", ""];
  return bullets.slice(0, 4).map((text, i) => {
    const local = localItems[i] || {};
    const title = text.split(/[:.]/)[0].slice(0, 50) || local.title || `Signal ${i + 1}`;
    return {
      title: title.length > 48 ? `${title.slice(0, 45)}…` : title,
      body: text,
      impact: local.impact || impacts[i] || "Medium",
      iconClass: local.iconClass || iconClasses[i] || "",
    };
  });
};

export const bulletsToUserInsightCards = (bullets, localCards = []) => {
  if (!bullets?.length) return null;
  const iconClasses = ["", "green", "purple"];
  return bullets.slice(0, 3).map((text, i) => {
    const local = localCards[i] || {};
    const title = text.split(/[:.]/)[0].slice(0, 50) || local.title || `Insight ${i + 1}`;
    return {
      iconClass: local.iconClass || iconClasses[i] || "",
      title: title.length > 48 ? `${title.slice(0, 45)}…` : title,
      description: text,
      rows: local.rows || [{ text: text, className: "" }],
    };
  });
};

export const bulletsToUsmActionQueue = (bullets, localActions = []) => {
  if (!bullets?.length) return null;
  const tones = ["purple", "", "orange"];
  return bullets.slice(0, 4).map((text, i) => {
    const local = localActions[i] || {};
    const splitAt = text.indexOf(":");
    const title =
      (splitAt > 0 ? text.slice(0, splitAt) : text.split(/[.]/)[0]).slice(0, 55) ||
      local.title ||
      `Action ${i + 1}`;
    const body = splitAt > 0 ? text.slice(splitAt + 1).trim() : text;
    return {
      num: local.num || i + 1,
      tone: local.tone || tones[i] || "",
      title: title.length > 52 ? `${title.slice(0, 49)}…` : title,
      text: body || local.text || text,
    };
  });
};

export const bulletsToExecutiveInsightCards = (bullets, localCards = []) => {
  if (!bullets?.length) return null;
  const iconClasses = ["red", "green", "purple", "orange"];
  return bullets.slice(0, 4).map((text, i) => {
    const local = localCards[i] || {};
    const title = text.split(/[:.]/)[0].slice(0, 55) || local.title || `Insight ${i + 1}`;
    return {
      id: local.id || `ai-${i}`,
      title: title.length > 52 ? `${title.slice(0, 49)}…` : title,
      body: text,
      iconClass: local.iconClass || iconClasses[i] || "",
    };
  });
};

export const bulletsToSqafInsightCards = (bullets, localCards = []) => {
  if (!bullets?.length) return null;
  const tones = ["red", "orange", "green", "purple"];
  return bullets.slice(0, 4).map((text, i) => {
    const local = localCards[i] || {};
    const splitAt = text.indexOf(":");
    const title =
      (splitAt > 0 ? text.slice(0, splitAt) : text.split(/[.]/)[0]).trim().slice(0, 55) ||
      local.title ||
      `Insight ${i + 1}`;
    const body = splitAt > 0 ? text.slice(splitAt + 1).trim() : text;
    return {
      tone: local.tone || tones[i] || "purple",
      title: title.length > 52 ? `${title.slice(0, 49)}…` : title,
      body: body || local.body || text,
    };
  });
};

export const bulletsToSqafActionItems = (bullets, localItems = []) => {
  if (!bullets?.length) return null;
  const tones = ["red", "orange", "green", "blue", "purple"];
  const priorities = ["High", "Medium", "Planned", "Routine"];
  const priorityClasses = ["", "amber", "blue", "blue"];
  return bullets.slice(0, 4).map((text, i) => {
    const local = localItems[i] || {};
    const splitAt = text.indexOf(":");
    const title =
      (splitAt > 0 ? text.slice(0, splitAt) : text.split(/[.]/)[0]).trim().slice(0, 55) ||
      local.title ||
      `Action ${i + 1}`;
    const body = splitAt > 0 ? text.slice(splitAt + 1).trim() : text;
    const priority = local.priority || priorities[i] || "Medium";
    return {
      tone: local.tone || tones[i] || "orange",
      title: title.length > 52 ? `${title.slice(0, 49)}…` : title,
      body: body || local.body || text,
      priority,
      priorityClass: local.priorityClass ?? priorityClasses[i] ?? "amber",
    };
  });
};

export const bulletsToSqafRowField = (bullets, localRows = [], field = "note") => {
  if (!bullets?.length) return null;
  return localRows.map((row, i) => ({
    ...row,
    [field]: bullets[i] || row[field],
  }));
};

export const bulletsToSqafSchoolActionQueue = (bullets, localQueue = { pill: "", items: [] }) => {
  if (!bullets?.length) return null;
  const mapped = bulletsToSqafActionItems(bullets, localQueue.items);
  if (!mapped?.length) return null;
  return { pill: localQueue.pill, items: mapped };
};
